
import React, { useMemo, useState } from "react";
import {
  CreditCard,
  Plus,
  Search,
  Pencil,
  Trash2,
  Power,
  X,
  Check,
  Clock3,
  IndianRupee,
  RotateCcw,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const initialPlans = [
  {
    id: 1,
    name: "Basic Plan",
    code: "PLAN001",
    duration: 12,
    price: 1000,
    description: "Essential membership for getting started.",
    features: [
      "Unlimited View member profiles" 
    ],
    status: "Active",
}
];

const emptyPlan = {
  name: "",
  duration: "12",
  price: "",
  description: "",
  featuresText: "",
};

function formatPrice(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

function StatusBadge({ status }) {
  const active = status === "Active";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold ${
        active
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? "bg-emerald-500" : "bg-slate-400"
        }`}
      />
      {status}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, color, description }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">{title}</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-800">
            {value}
          </h3>
          <p className="mt-2 text-[11px] text-slate-400">
            {description}
          </p>
        </div>

        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${colors[color]}`}
        >
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

function PlanModal({ plan, onClose, onSave }) {
  const [form, setForm] = useState(() =>
    plan
      ? {
          name: plan.name,
          duration: String(plan.duration),
          price: String(plan.price),
          description: plan.description,
          featuresText: plan.features.join("\n"),
        }
      : emptyPlan
  );

  const [error, setError] = useState("");

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("Please enter a plan name.");
      return;
    }

    if (Number(form.price) < 0 || form.price === "") {
      setError("Please enter a valid plan price.");
      return;
    }

    const features = form.featuresText
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);

    onSave({
      name: form.name.trim(),
      duration: Number(form.duration),
      price: Number(form.price),
      description: form.description.trim(),
      features,
    });
  };

  const inputClass =
    "mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-900/50 p-3 sm:p-5">
      <div className="my-auto w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {plan ? "Edit Membership Plan" : "Add Membership Plan"}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Configure plan pricing, duration and benefits.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={19} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 p-5 sm:p-6">
            <div>
              <label className="text-xs font-semibold text-slate-600">
                Plan Name *
              </label>
              <input
                required
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="e.g. Gold Plan"
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold text-slate-600">
                  Duration *
                </label>
                <select
                  required
                  value={form.duration}
                  onChange={(e) => updateField("duration", e.target.value)}
                  className={inputClass}
                >
                  <option value="1">1 Month</option>
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="12">12 Months</option>
                  <option value="24">24 Months</option>
                  <option value="36">36 Months</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">
                  Plan Price (₹) *
                </label>
                <input
                  required
                  type="number"
                  min="0"
                  step="1"
                  value={form.price}
                  onChange={(e) => updateField("price", e.target.value)}
                  placeholder="Enter price"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">
                Description
              </label>
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Briefly describe this plan"
                className={inputClass}
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">
                Plan Benefits
              </label>
              <textarea
                rows={5}
                value={form.featuresText}
                onChange={(e) =>
                  updateField("featuresText", e.target.value)
                }
                placeholder={"Enter one benefit per line\nView member profiles\nContact details access"}
                className={inputClass}
              />
              <p className="mt-1.5 text-[10px] text-slate-400">
                Enter each benefit on a separate line.
              </p>
            </div>

            {error && (
              <p role="alert" className="rounded-lg bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
                {error}
              </p>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {plan ? "Save Changes" : "Create Plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteModal({ plan, onClose, onConfirm }) {
  if (!plan) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
          <Trash2 size={22} />
        </div>

        <h3 className="mt-4 text-center text-lg font-bold text-slate-800">
          Delete Membership Plan?
        </h3>

        <p className="mt-2 text-center text-sm text-slate-500">
          Are you sure you want to delete{" "}
          <strong>{plan.name}</strong> from the sample list?
        </p>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MembershipPlans() {
  const [plans, setPlans] = useState(initialPlans);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletePlan, setDeletePlan] = useState(null);
  const [notice, setNotice] = useState("");

  const stats = useMemo(
    () => [
      {
        title: "Total Plans",
        value: plans.length,
        icon: CreditCard,
        color: "blue",
        description: "All membership plans",
      },
      {
        title: "Active Plans",
        value: plans.filter((p) => p.status === "Active").length,
        icon: Check,
        color: "green",
        description: "Available for purchase",
      },
      {
        title: "Inactive Plans",
        value: plans.filter((p) => p.status === "Inactive").length,
        icon: Power,
        color: "amber",
        description: "Unavailable for purchase",
      },
      {
        title: "Highest Plan Price",
        value: formatPrice(Math.max(0, ...plans.map((p) => p.price))),
        icon: IndianRupee,
        color: "violet",
        description: "Maximum configured price",
      },
    ],
    [plans]
  );

  const filteredPlans = useMemo(() => {
    const term = search.trim().toLowerCase();

    return plans.filter((plan) => {
      const matchesSearch =
        !term ||
        plan.name.toLowerCase().includes(term) ||
        plan.code.toLowerCase().includes(term) ||
        plan.description.toLowerCase().includes(term);

      return matchesSearch && (!statusFilter || plan.status === statusFilter);
    });
  }, [plans, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredPlans.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const visiblePlans = filteredPlans.slice(startIndex, startIndex + pageSize);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const savePlan = (form) => {
    if (editingPlan) {
      setPlans((current) =>
        current.map((plan) =>
          plan.id === editingPlan.id ? { ...plan, ...form } : plan
        )
      );
      setNotice("Membership plan updated successfully.");
    } else {
      const nextNumber =
        Math.max(
          0,
          ...plans.map((plan) =>
            Number(plan.code.replace("PLAN", "")) || 0
          )
        ) + 1;

      setPlans((current) => [
        {
          ...form,
          id: Date.now(),
          code: `PLAN${String(nextNumber).padStart(3, "0")}`,
          status: "Active",
        },
        ...current,
      ]);

      setNotice("Membership plan created successfully.");
      setPage(1);
    }

    setModalOpen(false);
    setEditingPlan(null);
  };

  const toggleStatus = (plan) => {
    const nextStatus = plan.status === "Active" ? "Inactive" : "Active";

    setPlans((current) =>
      current.map((item) =>
        item.id === plan.id ? { ...item, status: nextStatus } : item
      )
    );

    setNotice(
      `${plan.name} is now ${nextStatus.toLowerCase()}.`
    );
  };

  const confirmDelete = () => {
    setPlans((current) =>
      current.filter((plan) => plan.id !== deletePlan.id)
    );
    setNotice("Membership plan deleted from the sample list.");
    setDeletePlan(null);
  };

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Home</span>
            <span>/</span>
            <span>Membership Plans</span>
            <span>/</span>
            <span className="font-medium text-blue-600">Plan List</span>
          </div>

          <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">
            Membership Plans
          </h1>

          <p className="mt-1 text-xs text-slate-500 sm:text-sm">
            Create and manage membership packages and pricing.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingPlan(null);
            setModalOpen(true);
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
        >
          <Plus size={17} />
          Add Membership Plan
        </button>
      </div>

      {/* Notification */}
      {notice && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>

      {/* Search and filters */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4">
          <h2 className="text-sm font-bold text-slate-800">
            Search & Filters
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            Search plans by name or plan code.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search plan name, code or description"
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">
            Found{" "}
            <strong className="text-slate-800">
              {filteredPlans.length}
            </strong>{" "}
            plan(s)
          </p>

          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <RotateCcw size={14} />
            Reset Filters
          </button>
        </div>
      </section>

      {/* Plan list */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="text-sm font-bold text-slate-800">
              Membership Plan List
            </h2>
            <p className="mt-1 text-[11px] text-slate-400">
              Manage plan details, availability and pricing.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500">
            Show
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            entries
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px] text-left">
            <thead className="bg-slate-50">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Plan</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Benefits</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {visiblePlans.map((plan) => (
                <tr key={plan.id} className="transition hover:bg-slate-50/70">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                        <CreditCard size={19} />
                      </div>
                      <div>
                        <p className="whitespace-nowrap text-xs font-semibold text-slate-800">
                          {plan.name}
                        </p>
                        <p className="mt-1 text-[10px] text-slate-400">
                          {plan.code}
                        </p>
                        {plan.description && (
                          <p className="mt-1 max-w-[240px] text-[10px] text-slate-500">
                            {plan.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Clock3 size={14} className="text-slate-400" />
                      {plan.duration} {plan.duration === 1 ? "Month" : "Months"}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <p className="text-sm font-bold text-slate-800">
                      {formatPrice(plan.price)}
                    </p>
                    <p className="mt-1 text-[10px] text-slate-400">
                      One-time price
                    </p>
                  </td>

                  <td className="px-4 py-4">
                    <div className="space-y-1.5">
                      {plan.features.slice(0, 3).map((feature) => (
                        <div
                          key={feature}
                          className="flex items-start gap-1.5 text-[10px] text-slate-600"
                        >
                          <Check
                            size={12}
                            className="mt-0.5 shrink-0 text-emerald-500"
                          />
                          <span>{feature}</span>
                        </div>
                      ))}
                      {plan.features.length > 3 && (
                        <p className="pl-4 text-[10px] font-medium text-blue-600">
                          +{plan.features.length - 3} more benefits
                        </p>
                      )}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-4 py-4">
                    <StatusBadge status={plan.status} />
                  </td>

                  <td className="whitespace-nowrap px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title={plan.status === "Active" ? "Deactivate plan" : "Activate plan"}
                        aria-label={`${plan.status === "Active" ? "Deactivate" : "Activate"} ${plan.name}`}
                        onClick={() => toggleStatus(plan)}
                        className={`rounded-lg p-2 ${
                          plan.status === "Active"
                            ? "text-rose-600 hover:bg-rose-50"
                            : "text-emerald-600 hover:bg-emerald-50"
                        }`}
                      >
                        <Power size={15} />
                      </button>

                      <button
                        type="button"
                        title="Edit plan"
                        aria-label={`Edit ${plan.name}`}
                        onClick={() => {
                          setEditingPlan(plan);
                          setModalOpen(true);
                        }}
                        className="rounded-lg p-2 text-amber-600 hover:bg-amber-50"
                      >
                        <Pencil size={15} />
                      </button>

                      <button
                        type="button"
                        title="Delete plan"
                        aria-label={`Delete ${plan.name}`}
                        onClick={() => setDeletePlan(plan)}
                        className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {visiblePlans.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-14 text-center">
                    <CreditCard size={28} className="mx-auto text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-700">
                      No membership plans found
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      Try another search or change the status filter.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-[11px] text-slate-500">
            Showing {filteredPlans.length ? startIndex + 1 : 0} to{" "}
            {Math.min(startIndex + pageSize, filteredPlans.length)} of{" "}
            {filteredPlans.length} entries
          </p>

          <div className="flex items-center gap-1 self-end sm:self-auto">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (number) =>
                  number === 1 ||
                  number === totalPages ||
                  Math.abs(number - safePage) <= 1
              )
              .map((number, index, numbers) => (
                <React.Fragment key={number}>
                  {index > 0 && number - numbers[index - 1] > 1 && (
                    <span className="px-1 text-xs text-slate-400">...</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setPage(number)}
                    className={`h-8 min-w-8 rounded-lg px-2 text-xs font-medium ${
                      safePage === number
                        ? "bg-blue-600 text-white"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {number}
                  </button>
                </React.Fragment>
              ))}

            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {modalOpen && (
        <PlanModal
          plan={editingPlan}
          onClose={() => {
            setModalOpen(false);
            setEditingPlan(null);
          }}
          onSave={savePlan}
        />
      )}

      <DeleteModal
        plan={deletePlan}
        onClose={() => setDeletePlan(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
